// crewbit-admin/src/lib/errorMap.ts
//
// Money Module RPC error mapping for the admin operator dashboard. Mirrors
// crewbit-mobile/lib/errorMessages.ts Layer 2. Admin has no legacy Q8 substrate —
// this file is fresh.
//
// Spec: docs/references/MONEY_MODULE_WIRING_v1.md §4.

import { scheduleErrorNamespaces, scheduleDefaultErrorMessages, type ScheduleErrorCode } from './scheduleErrorMap';
import { siteErrorNamespaces, siteDefaultErrorMessages, type SiteErrorCode } from './errorMapSite';
export type { ScheduleErrorCode, ScheduleError, ConflictDetail } from './scheduleErrorMap';
export type { SiteErrorCode } from './errorMapSite';
export { extractErrorCode, extractErrorMeta } from './errorMapSite'; // §3.2.0 helpers

/** Stable error codes returned by Money RPCs (DATA spec §5) + cross-cutting infra codes. */
export type MoneyErrorCode =
  // Per-RPC business codes
  | 'FORBIDDEN'
  | 'CLIENT_NOT_FOUND'
  | 'INVALID_LINE_ITEMS'
  | 'INVALID_DATE_RANGE'
  | 'INVALID_TAX_RATE'
  | 'BUSINESS_NOT_ACTIVE'
  | 'BUSINESS_NOT_FOUND'
  | 'INVOICE_NOT_EDITABLE'
  | 'INVOICE_NOT_CANCELLABLE'
  | 'INVOICE_ALREADY_CANCELLED'
  | 'INVOICE_NOT_FOUND'
  | 'INSUFFICIENT_AGE_FOR_BAD_DEBT'
  | 'NOT_A_MEMBER'
  | 'INVALID_SESSION_TOKEN'
  | 'EXPENSE_NOT_FOUND'
  | 'VENDOR_BILL_NOT_FOUND'
  | 'REMITTANCE_NOT_FILED'
  | 'INVOICE_NOT_OUTSTANDING'
  | 'NO_REMINDER_CHANNEL'
  | 'INVALID_CHANNEL'
  // §InvoiceDetail substrate codes (Session §InvoiceDetailSubstrate)
  | 'INVOICE_NOT_PAID'
  | 'NO_RECEIPT_CHANNEL'
  | 'INVOICE_NOT_REFUNDABLE'
  | 'AMOUNT_EXCEEDS_REMAINING'
  | 'AMOUNT_MUST_BE_POSITIVE'
  | 'STRIPE_INVOICE_USE_STRIPE_REFUND'
  // §InvoiceAuthoring substrate codes (Session §InvoiceAuthoringSubstrate)
  | 'QUOTE_NOT_FOUND'
  | 'QUOTE_NOT_APPROVED'
  | 'QUOTE_HAS_NO_LINE_ITEMS'
  // §InvoiceAuthoring panel codes (Session 127). Verified against live RPC bodies
  // (migration 237 mark_invoice_sent). Mobile parallel filed as
  // §InvoiceAuthoring.MobileErrorCodeOrphans — mobile shipped imagined codes
  // (INVOICE_NOT_DRAFT/INVOICE_NOT_PAYABLE/INVALID_AMOUNT) that no live RPC returns.
  | 'INVOICE_NOT_SENDABLE'
  | 'INVALID_SENT_VIA'
  // §Expenses panel codes (Session 129). Verified live against record_expense +
  // update_expense RPC bodies (Cowork 2026-05-02).
  | 'INVALID_AMOUNT'
  | 'INVALID_CATEGORY'
  | 'PHASE_NOT_FOUND'
  | 'PHASE_PROJECT_MISMATCH'
  // §Tax destination substrate codes (Session 134 — mark_remittance_filed +
  // generate_tax_summary, live-verified against migration 268 RPC bodies).
  // REMITTANCE_NOT_FOUND replaces existing reopen_tax_remittance misuse of
  // INVOICE_NOT_FOUND for tax remittance lookups; existing body cleanup
  // deferred per §Tax.ReopenRemittanceErrorCodeFix.
  | 'REMITTANCE_NOT_FOUND'
  | 'REMITTANCE_ALREADY_FILED'
  | 'INVALID_PERIOD'
  | 'BUSINESS_HSTNUMBER_REQUIRED'
  // §Settings hub substrate codes (Session 137 — update_user_money_preferences
  // + update_business_money_settings + generate-money-export edge fn,
  // live-verified against migration 269 + edge fn body).
  // INELIGIBLE_FOR_QUICK_METHOD shipped forward-compat — eligibility check
  // deferred per §Tax.QuickMethodEligibilityServerSide.
  // INVALID_EXPORT_TYPE is edge-fn-only (not in any RPC body).
  | 'INELIGIBLE_FOR_QUICK_METHOD'
  | 'INVALID_FISCAL_YEAR_START'
  | 'INVALID_MILEAGE_RATE'
  | 'INVALID_HSTNUMBER_FORMAT'
  | 'INVALID_EXPORT_TYPE'
  | 'EXPORT_TYPE_NOT_AVAILABLE'
  | 'EXPORT_RATE_LIMITED'
  // §WorldClass.4 onboarding-as-defaults-source codes (Tier 2 Phase A —
  // bootstrap_business_defaults, live-verified against Migration 272 RPC body).
  | 'INVALID_PROVINCE'
  | 'INVALID_TRADE'
  | 'INVALID_CRA_BN_FORMAT'
  // §WorldClass.3 tax automation codes (Tier 2 Phase B — propose_expense_categorization
  // + confirm_expense_proposal, live-verified against Migration 273 RPC bodies).
  | 'INVALID_ACTION'
  | 'PROPOSAL_NOT_FOUND'
  | 'PROPOSAL_EXPIRED'
  | 'PROPOSAL_ALREADY_CONFIRMED'
  // §WorldClass.2 Quick Method auto-derive codes (Tier 2 Phase C — derive_quick_method_config
  // + update_business_money_settings extended, live-verified against Migration 274 RPC bodies).
  | 'QUEBEC_QM_NOT_SUPPORTED_V1'
  | 'INVALID_QUICK_METHOD_RATE'
  // §WorldClass.1 per-client overrides codes (Tier 2 Phase D — get_client_defaults +
  // create_invoice extended, live-verified against Migration 275 RPC bodies).
  // CLIENT_NOT_FOUND already declared above; no new codes needed for Phase D.
  // Tier 3 Phase C (Migration 279) — update_invoice arg parity with create_invoice's
  // Phase D extension. Inline validation codes (invoices lacks DB CHECK; mirrors
  // clients_default_late_fee_polymorphic + deposit + payment_terms shape).
  | 'INVALID_PAYMENT_TERMS_DAYS'
  | 'INVALID_DEPOSIT_PERCENTAGE'
  | 'INVALID_LATE_FEE_POLICY'
  | 'INVALID_LATE_FEE_AMOUNT'
  // §WorldClass.5 Stripe Connect deep codes (Tier 2 Phase E — enable_stripe_connect_features
  // + get_dispute_escalations + update_business_money_settings extended; live-verified
  // against Migration 276 RPC bodies).
  | 'STRIPE_CONNECT_NOT_READY'
  | 'UNKNOWN_FEATURE_KEY'
  | 'INVALID_STATUS_FILTER'
  // §Wave-A Phase C codes — §FinancialSettings.LegacyDirectWriteRetirement
  // (update_business_money_settings extended 11→15 args; live-verified
  // against Migration 284 RPC body).
  | 'INVALID_HST_RATE'
  | 'INVALID_VEHICLE_BUSINESS_USE'
  | 'INVALID_TELECOM_BUSINESS_USE'
  | 'INVALID_HOME_OFFICE_PERCENTAGE' | 'INVALID_HST_FILING_FREQUENCY' // Today Hub Spec v1 PD-1 (Mig 20261103232558): p_hst_filing_frequency
  // §Money.DraftDiscardActuallyDeletes (2026-05-06) — delete_draft_invoice RPC.
  | 'INVOICE_NOT_DRAFT'
  | 'INVOICE_HAS_PAYMENT_LEDGER'
  | 'INVALID_PAYMENT_METHOD' | 'BOOKING_NOT_FOUND' // record_booking_payment (Mig 294)
  // §Work Phase A (§T.110) + Phase B (§T.111) change-order codes
  | 'QUOTE_ALREADY_CONVERTED' | 'QUOTE_NOT_CHANGEABLE' | 'MESSAGE_REQUIRED' | 'MESSAGE_TOO_LONG'
  | 'ONE_PARENT_REQUIRED' | 'PARENT_NOT_FOUND' | 'CHANGE_ORDER_NOT_FOUND'
  | 'CHANGE_ORDER_NOT_EDITABLE' | 'CHANGE_ORDER_NOT_RESPONDABLE' | 'INVALID_OUTCOME'
  // Cross-cutting infrastructure codes
  | 'NETWORK_FAILURE'
  | 'AUTH_EXPIRED'
  | 'RLS_DENIED'
  | 'NO_ACTIVE_BUSINESS'
  | 'STALE_PUBLIC_USER_ID'
  // §Money.StubActionSweepCompletion Phase 3 — create_vendor codes (Migration 291).
  | 'BUSINESS_MISMATCH'
  | 'COMPANY_NAME_REQUIRED'
  | 'COMPANY_NAME_TOO_LONG'
  | 'INVALID_PAYMENT_TERMS'
  // §Money.StubActionSweepCompletion Phase 4 — update_invoice_settings codes (Migration 292).
  | 'INVALID_LATE_FEE_TYPE'
  | 'INVALID_ACCEPTED_METHODS'
  | 'UNKNOWN'; // Phase F Site codes split into errorMapSite.ts

export type MoneyError = {
  code: MoneyErrorCode | SiteErrorCode;
  /** Internal description for Sentry — never shown to user. */
  message?: string;
  /** Optional: which input field the error relates to. Drives inline vs toast placement. */
  field?: string;
  /** Optional retry hints / dispute deadlines / etc. */
  meta?: Record<string, unknown>;
};


/**
 * Per-RPC error copy. Identical content to mobile + web; kept duplicated by surface
 * since the three runtimes are different (RN, Vite-React, Vite-React-JS) and a shared
 * package would buy little against three small files.
 */
const errorMessagesByRpc: Record<string, Partial<Record<MoneyErrorCode | ScheduleErrorCode | SiteErrorCode, string>>> = {
  ...scheduleErrorNamespaces, ...siteErrorNamespaces, // §T.93 + §T.115 (extracted)
  create_invoice: {
    FORBIDDEN: "You don't have permission to create invoices for this business.",
    CLIENT_NOT_FOUND: "Couldn't find that client. Try refreshing your client list.",
    INVALID_LINE_ITEMS: "Check your line items — at least one is missing a required field.",
    INVALID_DATE_RANGE: "Due date can't be before issue date.",
    INVALID_TAX_RATE: "Tax rate must be between 0% and 100%.",
    BUSINESS_NOT_ACTIVE: "Switch to an active business to create invoices.",
  },
  update_invoice: {
    INVOICE_NOT_EDITABLE: "Only draft invoices can be edited. Cancel and re-create instead.",
    FORBIDDEN: "You don't have permission to edit this invoice.",
    INVOICE_NOT_FOUND: "That invoice no longer exists.",
    INVALID_PAYMENT_TERMS_DAYS: "Payment terms must be zero days or more.",
    INVALID_DEPOSIT_PERCENTAGE: "Deposit must be between 0% and 100%.",
    INVALID_LATE_FEE_POLICY: "Late fee policy must be either percent or flat.",
    INVALID_LATE_FEE_AMOUNT: "Late fee amount doesn't match the policy. Percent fees take 0–100; flat fees take any amount above zero.",
  },
  cancel_invoice: {
    FORBIDDEN: "Only owners and managers can cancel invoices.",
    INVOICE_NOT_CANCELLABLE: "This invoice has been refunded. Use the refund flow instead.",
    INVOICE_ALREADY_CANCELLED: "Already cancelled.",
    INVOICE_NOT_FOUND: "That invoice no longer exists.",
  },
  delete_draft_invoice: {
    NO_ACTIVE_BUSINESS: "Pick a business first, then delete drafts.",
    FORBIDDEN: "Only owners and managers can delete drafts.",
    INVOICE_NOT_FOUND: "That draft no longer exists.",
    INVOICE_NOT_DRAFT: "This invoice was sent before delete completed. Refresh and try again.",
    INVOICE_HAS_PAYMENT_LEDGER: "This draft has payment activity attached. Use the cancel flow instead.",
  },
  mark_invoice_bad_debt: {
    FORBIDDEN: "Only owners and managers can write off bad debt.",
    INSUFFICIENT_AGE_FOR_BAD_DEBT: "Wait until this invoice is at least 90 days overdue before writing it off.",
  },
  duplicate_invoice_as_draft: {
    NO_ACTIVE_BUSINESS: "Pick a business first, then duplicate invoices.",
    FORBIDDEN: "Only owners and managers can duplicate invoices.",
    INVOICE_NOT_FOUND: "That invoice no longer exists.",
  },
  create_vendor: {
    NO_ACTIVE_BUSINESS: "Pick a business first, then add a vendor.",
    FORBIDDEN: "Only owners and managers can add vendors.",
    BUSINESS_MISMATCH: "That vendor would belong to a different business.",
    COMPANY_NAME_REQUIRED: "Enter a company name to continue.",
    COMPANY_NAME_TOO_LONG: "Company name is too long — keep it under 200 characters.",
    INVALID_PAYMENT_TERMS: "Payment terms must be net_15, net_30, net_60, or on_receipt.",
  },
  update_invoice_settings: {
    NO_ACTIVE_BUSINESS: "Pick a business first, then update payment settings.",
    FORBIDDEN: "Only owners and managers can change payment settings.",
    BUSINESS_MISMATCH: "These settings belong to a different business.",
    INVALID_LATE_FEE_TYPE: "Late-fee type must be none, percent, or flat.",
    INVALID_ACCEPTED_METHODS: "Accepted methods must be a list of payment methods.",
  },
  record_expense: {
    FORBIDDEN: "You don't have permission to log expenses for this business.",
    BUSINESS_NOT_ACTIVE: "Switch to an active business to log expenses.",
    INVALID_AMOUNT: "Enter an amount greater than zero.",
    INVALID_CATEGORY: "Pick a category before saving.",
    PHASE_NOT_FOUND: "That phase no longer exists. Pick another or clear the link.",
    PHASE_PROJECT_MISMATCH: "That phase belongs to a different project. Re-link to a matching project.",
  },
  update_expense: {
    EXPENSE_NOT_FOUND: "That expense no longer exists.",
    FORBIDDEN: "You don't have permission to edit this expense.",
    INVALID_AMOUNT: "Enter an amount greater than zero.",
    PHASE_NOT_FOUND: "That phase no longer exists. Pick another or clear the link.",
    PHASE_PROJECT_MISMATCH: "That phase belongs to a different project. Re-link to a matching project.",
  },
  record_expense_payment: {
    VENDOR_BILL_NOT_FOUND: "That bill no longer exists.",
  },
  create_vendor_bill: {
    FORBIDDEN: "You don't have permission to add bills for this business.",
  },
  set_active_business: {
    NOT_A_MEMBER: "You don't have access to that business.",
    INVALID_SESSION_TOKEN: "Couldn't switch business — restart the app and try again.",
    BUSINESS_NOT_FOUND: "That business doesn't exist or has been deleted.",
  },
  set_invoice_paid_manually: {
    INVOICE_NOT_FOUND: "That invoice no longer exists.",
  },
  // Session 127 §InvoiceAuthoring panel — mark_invoice_sent (migration 237).
  // record_manual_invoice_payment uses RAISE EXCEPTION (not structured codes);
  // mapPostgresError handles its raw exception strings, so no namespace here.
  mark_invoice_sent: {
    INVOICE_NOT_SENDABLE: "This invoice can't be sent in its current state.",
    INVOICE_NOT_FOUND: "Couldn't find that invoice. Try refreshing the list.",
    INVALID_SENT_VIA: "That delivery channel isn't supported.",
  },
  reopen_tax_remittance: {
    FORBIDDEN: "Only owners can reopen filed remittances.",
    REMITTANCE_NOT_FILED: "This remittance hasn't been filed yet — nothing to reopen.",
    REMITTANCE_NOT_FOUND: "That tax remittance no longer exists.",
  },
  // Session 134 §Tax destination substrate.
  mark_remittance_filed: {
    FORBIDDEN: "Only owners and managers can file remittances.",
    INVALID_PERIOD: "Period end can't be before period start.",
    BUSINESS_HSTNUMBER_REQUIRED: "Add your HST number in Settings before filing an HST remittance.",
    REMITTANCE_ALREADY_FILED: "This period is already filed. Reopen it first to amend.",
  },
  generate_tax_summary: {
    FORBIDDEN: "You don't have permission to view tax summaries for this business.",
    INVALID_PERIOD: "Period end can't be before period start.",
  },
  // Session 137 §Settings hub substrate.
  update_user_money_preferences: {
    STALE_PUBLIC_USER_ID: "Sign out and back in, then try again.",
  },
  update_business_money_settings: {
    FORBIDDEN: "Only owners and managers can change business settings.",
    INVALID_MILEAGE_RATE: "Mileage rate must be between 0 and 1.00.",
    INVALID_HSTNUMBER_FORMAT: "HST number must be 9 digits + RT + 4 digits (e.g. 123456789RT0001).",
    INVALID_FISCAL_YEAR_START: "Fiscal year start must be a valid month (1-12).",
    INELIGIBLE_FOR_QUICK_METHOD: "Quick Method requires revenue under $400k and at least 1 year of business history.",
    INVALID_QUICK_METHOD_RATE: "Quick Method rate must be between 0% and 100% (e.g. 8.8% = 0.088).",
    INVALID_HST_RATE: "HST rate must be between 0% and 100% (enter the percentage; we store it as a decimal).",
    INVALID_VEHICLE_BUSINESS_USE: "Vehicle business use must be between 0% and 100%.",
    INVALID_TELECOM_BUSINESS_USE: "Phone/internet business use must be between 0% and 100%.",
    INVALID_HOME_OFFICE_PERCENTAGE: "Home workspace percentage must be between 0% and 100%.", INVALID_HST_FILING_FREQUENCY: "Choose monthly, quarterly or annually.",
  },
  // generate-money-export is an edge function (not RPC) — reachable via
  // useDownloadMoneyExport. Codes lookup keyed under this name for symmetry.
  generate_money_export: {
    FORBIDDEN: "You don't have permission to export this business's data.",
    INVALID_EXPORT_TYPE: "That export type isn't supported.",
    EXPORT_TYPE_NOT_AVAILABLE: "This export isn't available yet — it requires Stripe Connect to be set up.",
    EXPORT_RATE_LIMITED: "Too many exports in the last hour. Try again later.",
  },
  financial_summary: {
    BUSINESS_NOT_ACTIVE: "Switch to an active business to see your money summary.",
  },
  send_reminder: {
    INVOICE_NOT_FOUND: "Couldn't find that invoice. Try refreshing the list.",
    INVOICE_NOT_OUTSTANDING: "Reminders only go out for outstanding invoices.",
    NO_REMINDER_CHANNEL: "Add an email for this client to send a reminder.",
    INVALID_CHANNEL: "Email is the only reminder channel right now.",
  },
  send_receipt: {
    INVOICE_NOT_FOUND: "Couldn't find that invoice. Try refreshing the list.",
    INVOICE_NOT_PAID: "Receipts only go out for paid invoices.",
    NO_RECEIPT_CHANNEL: "Add an email for this client to send a receipt.",
    INVALID_CHANNEL: "Email is the only receipt channel right now.",
  },
  mark_invoice_refunded_manually: {
    INVOICE_NOT_FOUND: "Couldn't find that invoice. Try refreshing the list.",
    INVOICE_NOT_REFUNDABLE: "Only paid or partially-refunded invoices can be refunded.",
    AMOUNT_EXCEEDS_REMAINING: "Refund amount can't exceed what's left to refund.",
    AMOUNT_MUST_BE_POSITIVE: "Refund amount must be greater than zero.",
    STRIPE_INVOICE_USE_STRIPE_REFUND: "This invoice was paid via Stripe. Use the Stripe refund flow instead.",
  },
  convert_quote_to_invoice: {
    QUOTE_NOT_FOUND: "Couldn't find that quote. Try refreshing the list.",
    QUOTE_NOT_APPROVED: "Only approved quotes can become invoices.",
    QUOTE_HAS_NO_LINE_ITEMS: "Add line items to the quote before converting.",
  },
  // Tier 2 Phase A §WorldClass.4 — onboarding-as-defaults-source substrate.
  bootstrap_business_defaults: {
    FORBIDDEN: "Only the business owner can bootstrap defaults.",
    INVALID_PROVINCE: "We need to know your province to set up tax defaults.",
    INVALID_TRADE: "Pick a trade to apply defaults — choose \"Other\" if yours isn't listed.",
    INVALID_CRA_BN_FORMAT: "Your CRA Business Number is 9 digits (the part before \"RT0001\").",
  },
  // Tier 2 Phase C §WorldClass.2 — Quick Method auto-derive substrate.
  derive_quick_method_config: {
    FORBIDDEN: "You don't have permission to view tax settings for this business.",
    QUEBEC_QM_NOT_SUPPORTED_V1: "Quebec's Quick Method runs through Revenu Québec — we'll add support for QC operators soon.",
  },
  // Tier 2 Phase D §WorldClass.1 — per-client overrides substrate.
  get_client_defaults: {
    FORBIDDEN: "You don't have permission to view client defaults for this business.",
    CLIENT_NOT_FOUND: "Couldn't find that client. Try refreshing your client list.",
  },
  // Tier 2 Phase E §WorldClass.5 — Stripe Connect deep substrate.
  enable_stripe_connect_features: {
    FORBIDDEN: "Only owners and managers can change Stripe Connect features.",
    STRIPE_CONNECT_NOT_READY: "Finish Stripe Connect setup before turning on tips.",
    UNKNOWN_FEATURE_KEY: "That feature isn't supported yet.",
  },
  get_dispute_escalations: {
    FORBIDDEN: "You don't have permission to view disputes for this business.",
    INVALID_STATUS_FILTER: "Pick a valid status filter.",
  },
  // Tier 2 Phase B §WorldClass.3 — tax automation substrate.
  propose_expense_categorization: {
    FORBIDDEN: "You don't have permission to categorize expenses for this business.",
    INVALID_AMOUNT: "Amount must be greater than zero.",
  },
  confirm_expense_proposal: {
    FORBIDDEN: "You don't have permission to confirm this proposal.",
    INVALID_ACTION: "That action isn't supported — pick confirm or decline.",
    PROPOSAL_NOT_FOUND: "That suggestion is no longer available — categorize manually.",
    PROPOSAL_EXPIRED: "Suggestions expire after 7 days — categorize manually instead.",
    PROPOSAL_ALREADY_CONFIRMED: "Already done — that suggestion was confirmed earlier.",
    INVALID_CATEGORY: "Pick a category from the list to continue.",
  },
};

const defaultErrorMessages: Record<MoneyErrorCode | ScheduleErrorCode | SiteErrorCode, string> = {
  ...scheduleDefaultErrorMessages,
  ...siteDefaultErrorMessages, // §T.115 Phase F
  FORBIDDEN: "You don't have permission to do this.",
  CLIENT_NOT_FOUND: "Couldn't find that client.",
  INVALID_LINE_ITEMS: "Check your inputs — something's missing or invalid.",
  INVALID_DATE_RANGE: "Check the dates — they don't line up.",
  INVALID_TAX_RATE: "Tax rate must be between 0% and 100%.",
  BUSINESS_NOT_ACTIVE: "Switch to an active business to continue.",
  BUSINESS_NOT_FOUND: "That business no longer exists.",
  BUSINESS_MISMATCH: "That belongs to a different business.",
  INVALID_PAYMENT_METHOD: "Payment method not supported. Use cash, card, or e-transfer.",
  BOOKING_NOT_FOUND: "Booking not found.",
  COMPANY_NAME_REQUIRED: "Enter a company name to continue.",
  COMPANY_NAME_TOO_LONG: "Company name is too long — keep it under 200 characters.",
  INVALID_PAYMENT_TERMS: "Payment terms must be net_15, net_30, net_60, or on_receipt.",
  INVALID_LATE_FEE_TYPE: "Late-fee type must be none, percent, or flat.",
  INVALID_ACCEPTED_METHODS: "Accepted methods must be a list of payment methods.",
  INVOICE_NOT_EDITABLE: "This invoice can't be edited in its current state.",
  INVOICE_NOT_CANCELLABLE: "This invoice can't be cancelled in its current state.",
  INVOICE_ALREADY_CANCELLED: "Already cancelled.",
  INVOICE_NOT_FOUND: "That invoice no longer exists.",
  INVOICE_NOT_DRAFT: "This invoice was sent before delete completed. Refresh and try again.",
  INVOICE_HAS_PAYMENT_LEDGER: "This invoice has payment activity attached.",
  INSUFFICIENT_AGE_FOR_BAD_DEBT: "Not yet eligible for bad-debt write-off.",
  NOT_A_MEMBER: "You don't have access to that.",
  INVALID_SESSION_TOKEN: "Couldn't establish session. Restart the app and try again.",
  EXPENSE_NOT_FOUND: "That expense no longer exists.",
  VENDOR_BILL_NOT_FOUND: "That bill no longer exists.",
  REMITTANCE_NOT_FILED: "Not yet filed.",
  INVOICE_NOT_OUTSTANDING: "Reminders only go out for outstanding invoices.",
  NO_REMINDER_CHANNEL: "Add an email for this client to send a reminder.",
  INVALID_CHANNEL: "That delivery channel isn't supported yet.",
  INVOICE_NOT_PAID: "This action only applies to paid invoices.",
  NO_RECEIPT_CHANNEL: "Add an email for this client to send a receipt.",
  INVOICE_NOT_REFUNDABLE: "This invoice can't be refunded in its current state.",
  AMOUNT_EXCEEDS_REMAINING: "That amount exceeds what's left to refund.",
  AMOUNT_MUST_BE_POSITIVE: "Amount must be greater than zero.",
  STRIPE_INVOICE_USE_STRIPE_REFUND: "This invoice was paid via Stripe. Use the Stripe refund flow instead.",
  QUOTE_NOT_FOUND: "Couldn't find that quote.",
  QUOTE_NOT_APPROVED: "Only approved quotes can be converted.",
  QUOTE_HAS_NO_LINE_ITEMS: "That quote has no line items to convert.",
  QUOTE_ALREADY_CONVERTED: "This quote was already converted to a job or project.",
  QUOTE_NOT_CHANGEABLE: "Changes can only be requested while the quote is still under review.",
  MESSAGE_REQUIRED: "Add a short note describing what you'd like changed.",
  MESSAGE_TOO_LONG: "Keep your note under 2,000 characters.",
  ONE_PARENT_REQUIRED: "Change orders must attach to exactly one job or project.",
  PARENT_NOT_FOUND: "Couldn't find that job or project.",
  CHANGE_ORDER_NOT_FOUND: "Couldn't find that change order.",
  CHANGE_ORDER_NOT_EDITABLE: "This change order can't be edited in its current state.",
  CHANGE_ORDER_NOT_RESPONDABLE: "This change order can't be approved or declined right now.",
  INVALID_OUTCOME: "Pick approve or decline.",
  INVOICE_NOT_SENDABLE: "This invoice can't be sent right now.",
  INVALID_SENT_VIA: "That delivery channel isn't supported yet.",
  INVALID_AMOUNT: "Enter an amount greater than zero.",
  INVALID_CATEGORY: "Pick a category before saving.",
  PHASE_NOT_FOUND: "That phase no longer exists.",
  PHASE_PROJECT_MISMATCH: "That phase doesn't belong to the linked project.",
  REMITTANCE_NOT_FOUND: "That tax remittance no longer exists.",
  REMITTANCE_ALREADY_FILED: "This period is already filed.",
  INVALID_PERIOD: "Period end can't be before period start.",
  BUSINESS_HSTNUMBER_REQUIRED: "Add your HST number in Settings to continue.",
  INELIGIBLE_FOR_QUICK_METHOD: "Not eligible for the Quick Method based on your business profile.",
  INVALID_FISCAL_YEAR_START: "Fiscal year start must be a valid month (1-12).",
  INVALID_MILEAGE_RATE: "Mileage rate must be between 0 and 1.00.",
  INVALID_HSTNUMBER_FORMAT: "HST number format is invalid.",
  INVALID_EXPORT_TYPE: "That export type isn't supported.",
  EXPORT_TYPE_NOT_AVAILABLE: "This export isn't available yet.",
  EXPORT_RATE_LIMITED: "Too many exports in the last hour.",
  INVALID_PROVINCE: "That province isn't supported yet.",
  INVALID_TRADE: "That trade isn't in our list — pick \"Other\" instead.",
  INVALID_CRA_BN_FORMAT: "CRA Business Number should be 9 digits.",
  INVALID_ACTION: "That action isn't supported.",
  PROPOSAL_NOT_FOUND: "That suggestion is no longer available.",
  PROPOSAL_EXPIRED: "That suggestion has expired.",
  PROPOSAL_ALREADY_CONFIRMED: "Already done.",
  QUEBEC_QM_NOT_SUPPORTED_V1: "Quebec Quick Method support is coming soon.",
  INVALID_QUICK_METHOD_RATE: "Quick Method rate must be between 0% and 100%.",
  INVALID_PAYMENT_TERMS_DAYS: "Payment terms must be zero days or more.",
  INVALID_DEPOSIT_PERCENTAGE: "Deposit must be between 0% and 100%.",
  INVALID_LATE_FEE_POLICY: "Late fee policy must be either percent or flat.",
  INVALID_LATE_FEE_AMOUNT: "Late fee amount doesn't match the policy.",
  STRIPE_CONNECT_NOT_READY: "Stripe Connect isn't set up yet for this business.",
  UNKNOWN_FEATURE_KEY: "That feature isn't supported yet.",
  INVALID_STATUS_FILTER: "Pick a valid status filter.",
  INVALID_HST_RATE: "HST rate must be between 0% and 100%.",
  INVALID_VEHICLE_BUSINESS_USE: "Vehicle business use must be between 0% and 100%.",
  INVALID_TELECOM_BUSINESS_USE: "Phone/internet business use must be between 0% and 100%.",
  INVALID_HOME_OFFICE_PERCENTAGE: "Home workspace percentage must be between 0% and 100%.", INVALID_HST_FILING_FREQUENCY: "Choose monthly, quarterly or annually.",
  NETWORK_FAILURE: "No connection. Try again when you're back online.",
  AUTH_EXPIRED: "Your session expired. Sign in again to continue.",
  RLS_DENIED: "You don't have access to that.",
  NO_ACTIVE_BUSINESS: "Pick a business first, then try again.",
  STALE_PUBLIC_USER_ID: "Your account needs to refresh. Sign out and back in, then try again.",
  UNKNOWN: "Something went wrong. Try again, or contact support if it keeps happening.",
};

/**
 * Look up user-facing copy for an RPC + error code pair. Falls back to the cross-cutting
 * default if the RPC doesn't override the code.
 */
export function lookupErrorCopy(rpcName: string, code: MoneyErrorCode | ScheduleErrorCode | SiteErrorCode): string {
  const rpcOverride = errorMessagesByRpc[rpcName]?.[code];
  if (rpcOverride) return rpcOverride;
  return defaultErrorMessages[code] ?? defaultErrorMessages.UNKNOWN;
}

/**
 * Normalize a Postgres / Supabase error object to a MoneyError discriminated shape.
 * Used by callMoneyRpc when the supabase.rpc call returns an `error` rather than a
 * discriminated `{success: false, error}` payload.
 */
export function mapPostgresError(error: unknown): MoneyError {
  const message =
    typeof error === 'object' && error !== null && 'message' in error
      ? String((error as { message: unknown }).message)
      : String(error);

  if (message.includes('Network request failed') || message.includes('Failed to fetch')) {
    return { code: 'NETWORK_FAILURE', message };
  }

  if (message.includes('JWT expired') || message.includes('invalid JWT')) {
    return { code: 'AUTH_EXPIRED', message };
  }

  if (message.includes('row-level security') || message.match(/new row violates.*policy/i)) {
    return { code: 'RLS_DENIED', message };
  }

  if (message.includes('invoices_created_by_fkey')
      || message.includes('expenses_logged_by_fkey')
      || message.includes('foreign key constraint')) {
    return { code: 'STALE_PUBLIC_USER_ID', message };
  }

  return { code: 'UNKNOWN', message };
}
